// Lead service using Supabase (Production - Supabase Required)
import { supabase } from './supabase';
import * as XLSX from 'xlsx'; // Import the library

export const saveLead = async (leadData) => {
  try {
    // Remove id if present (Supabase will auto-generate)
    const { id, timestamp, ...dataToInsert } = leadData;

    // Convert camelCase to snake_case for database columns
    const convertedData = {
      name: dataToInsert.name,
      email: dataToInsert.email,
      phone: dataToInsert.phone,
      type: dataToInsert.type,
      source: dataToInsert.source,
      message: dataToInsert.message || null,
      preferred_date: dataToInsert.preferred_date || dataToInsert.preferredDate || null,
      preferred_time: dataToInsert.preferred_time || dataToInsert.preferredTime || null,
      interested_country: dataToInsert.interested_country || dataToInsert.interestedCountry || null,
      course: dataToInsert.course || null,
    };

    const { data, error } = await supabase
      .from('leads')
      .insert([convertedData])
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error saving lead:', error);
    throw error;
  }
};

export const getAllLeads = async () => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching leads:', error);
    throw error;
  }
};

export const deleteLead = async (id) => {
  try {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting lead:', error);
    throw error;
  }
};

// Export to Excel (XLSX format)
export const exportLeadsToExcel = (leads) => {
  // 1. Map the data to clean, readable Column Headers
  const dataToExport = leads.map(lead => ({
    'Date': new Date(lead.created_at || lead.timestamp).toLocaleDateString(),
    'Time': new Date(lead.created_at || lead.timestamp).toLocaleTimeString(),
    'Name': lead.name || 'Unknown',
    'Email': lead.email || '-',
    'Phone': lead.phone || '-',
    'Type': lead.type ? lead.type.replace(/_/g, ' ').toUpperCase() : '-',
    'Source': lead.source ? lead.source.replace(/_/g, ' ') : 'Website',
    // These extra fields handle the specific data seen in your screenshot
    'Message': lead.message || '-',
    'Preferred Date': lead.preferred_date || '-',
    'Preferred Time': lead.preferred_time || '-',
    'Country': lead.country || '-',
    'Course': lead.course || '-'
  }));

  // 2. Create the Worksheet
  const worksheet = XLSX.utils.json_to_sheet(dataToExport);

  // 3. Set Column Widths (Auto-formatting)
  // This makes sure long emails/names don't get cut off or squashed
  const fitToColumn = (data, key) => {
      const max = data.reduce((w, r) => Math.max(w, (r[key] ? r[key].toString().length : 0)), 10);
      return { wch: max + 2 };
  };

  worksheet['!cols'] = [
    { wch: 12 }, // Date
    { wch: 10 }, // Time
    fitToColumn(dataToExport, 'Name'),
    fitToColumn(dataToExport, 'Email'),
    { wch: 15 }, // Phone
    { wch: 25 }, // Type
    { wch: 15 }, // Source
    { wch: 30 }, // Message (wider)
    { wch: 15 }, // Pref Date
    { wch: 10 }, // Pref Time
    { wch: 15 }, // Country
    { wch: 15 }  // Course
  ];

  // 4. Create Workbook and Export
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Leads Data");

  // 5. Trigger Download
  const fileName = `Leads_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
  
  return true;
};

// Export to PDF
export const exportLeadsToPDF = async (leads) => {
  if (!leads || leads.length === 0) {
    return null;
  }

  // Create HTML table for PDF
  const tableRows = leads.map((lead, index) => `
    <tr>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${index + 1}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.name || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.email || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.phone || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.type || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.source || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.preferred_date || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.preferred_time || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.interested_country || ''}</td>
      <td style="border: 1px solid #ddd; padding: 8px;">${lead.created_at ? new Date(lead.created_at).toLocaleDateString() : ''}</td>
    </tr>
  `).join('');

  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #D6001C; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
          th { background-color: #D6001C; color: white; padding: 10px; text-align: left; border: 1px solid #ddd; }
          td { border: 1px solid #ddd; padding: 8px; }
          .header { margin-bottom: 20px; }
          .date { color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>6th Sense Training Academy - Leads Export</h1>
          <p class="date">Generated on: ${new Date().toLocaleString()}</p>
          <p>Total Leads: ${leads.length}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Type</th>
              <th>Source</th>
              <th>Preferred Date</th>
              <th>Preferred Time</th>
              <th>Country</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `;

  // Use window.print() approach for PDF
  const printWindow = window.open('', '_blank');
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  
  // Wait for content to load, then print
  setTimeout(() => {
    printWindow.print();
  }, 250);
};

// Legacy CSV export (kept for backward compatibility)
export const exportLeadsToCSV = (leads) => {
  if (!leads || leads.length === 0) {
    return '';
  }

  const headers = Object.keys(leads[0]).join(',');
  const rows = leads.map((lead) => {
    return Object.values(lead)
      .map((value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value;
      })
      .join(',');
  });

  return [headers, ...rows].join('\n');
};
